import React from 'react';
import { __ } from 'artsteps2-common';
import TextureUploader from '../../../textures/TextureUploader';
import TextureList from '../../../textures/lists/TextureList';
import { compose, withState, withDispatch, withLifecycle } from '../../../../enhancers';
import { apiGET, setUIProperty, setUIData, setFormProperty } from '../../../../actions';
import {
  API_STATUS,
  getApiResource,
  getApiStatus,
  getAuthUser,
  getUIProperty,
  getFormProperty,
} from '../../../../reducers';
import colours from '../../../../styles/colours.json';

export const ExhibitionTexturesPanelView = ({
  textures = [],
  searchTerm = '',
  colour = '#000000',
  exhibitionId,
  ready = true,
  painting = false,
  placingTexture = false,
  onStartPainting = () => Promise.resolve(false),
  onStopPainting = () => Promise.resolve(false),
  onSearch = () => Promise.resolve(false),
}) => {
  const renderTextures = () => {
    const filteredTextures = textures.filter(
      ({ title = '', file: { bin = {} } = {} }) =>
        title.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1 ||
        (bin.name && bin.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1),
    );
    return (
      <TextureUploader>
        {textures.length === 0 && <div className="help-text">{__('help_textures')}</div>}
        {textures.length > 0 && filteredTextures.length === 0 && (
          <div className="help-text">{__('no_textures_found')}</div>
        )}
        <TextureList
          textures={filteredTextures}
          exhibitionId={exhibitionId}
          ready={ready}
          controls
        />
      </TextureUploader>
    );
  };

  const renderColours = () => (
    <div className="toolbar-section">
      <h4>
        {__('colours')} ({colours.basic.length})
      </h4>
      <div className="colours-list">
        {colours.basic.map(colourCode => (
          <div
            key={colourCode}
            className={`colour-wrapper item ${
              painting && colour === colourCode ? 'selected' : ''
            } ${placingTexture || (painting && colour !== colourCode) ? 'inactive' : ''}`}
          >
            <button
              className="colour"
              style={{ backgroundColor: colourCode }}
              onClick={() =>
                painting && colour === colourCode ? onStopPainting() : onStartPainting(colourCode)
              }
            >
              &nbsp;
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="exhibition-textures-editor">
      {renderColours()}
      <div className="toolbar-section">
        <h4>
          {__('textures')} ({textures.length})
          <div className="ui mini transparent left icon input">
            <input
              type="text"
              placeholder={__('search')}
              onChange={event => onSearch(event.target.value)}
            />
            <i className="search icon" />
          </div>
        </h4>
        {renderTextures()}
      </div>
    </div>
  );
};

const createQuery = () => ({});

const mapState = (state, { exhibitionId }) => {
  const currentUser = getAuthUser(state);
  return {
    currentUser,
    painting: getUIProperty(state, `exhibitions/${exhibitionId}/painting`),
    placingTexture: !!getUIProperty(state, `exhibitions/${exhibitionId}/placingTexture`),
    colour: getUIProperty(state, `exhibitions/${exhibitionId}/paintingColour`),
    textures: Object.values(getApiResource(state, 'textures', createQuery(currentUser))),
    ready: getApiStatus(state, 'textures', createQuery(currentUser)) === API_STATUS.IDLE,
    searchTerm: getFormProperty(state, 'textures', 'search'),
  };
};

const mapDispatch = (dispatch, { exhibitionId, painting, currentUser }) => ({
  onFetchTextures: () => dispatch(apiGET('textures', createQuery(currentUser))),
  onStartPainting: colour =>
    dispatch(
      setUIData(`exhibitions/${exhibitionId}`, {
        painting: true,
        placingTexture: undefined,
        paintingColour: colour,
      }),
    ),
  onStopPainting: () =>
    dispatch(
      setUIData(`exhibitions/${exhibitionId}`, {
        placingTexture: undefined,
        painting: false,
      }),
    ),
  onPaintAll: colour => dispatch(setUIProperty(`exhibitions/${exhibitionId}/wallColour`, colour)),
  onSearch: searchTerm => dispatch(setFormProperty('textures', 'search', searchTerm)),
});

const lifecycleMap = {
  onDidMount: ({ onFetchTextures }) => onFetchTextures(),
};

const ExhibitionTexturesPanel = compose(
  withState(mapState),
  withDispatch(mapDispatch),
  withLifecycle(lifecycleMap),
)(ExhibitionTexturesPanelView);

export default ExhibitionTexturesPanel;
